import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { TrendingUp, Flame, ChevronRight } from "lucide-react";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import BeatPlayer from "./BeatPlayer";
import LikeButton from "./LikeButton";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TrendingProducts = () => {
  const [trending, setTrending] = useState({ beats: [], merch: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchTrending = async () => {
      try {
        const res = await axios.get(`${API}/trending`);
        setTrending(res.data);
      } catch (e) {
        console.error("Error fetching trending:", e);
      } finally {
        setLoading(false);
      }
    };
    fetchTrending();
  }, []);

  if (loading) {
    return (
      <section className="py-20 px-6" data-testid="trending-section">
        <div className="max-w-7xl mx-auto">
          <div className="h-8 w-48 bg-zinc-800 animate-pulse rounded mb-8" />
          <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="aspect-[3/4] bg-zinc-900/50 animate-pulse rounded-sm" />
            ))}
          </div>
        </div>
      </section>
    );
  }

  if (trending.beats.length === 0 && trending.merch.length === 0) return null;

  return (
    <section className="py-20 px-6 bg-gradient-to-b from-transparent via-orange-500/5 to-transparent" data-testid="trending-section">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-10">
          <div>
            <Badge className="bg-orange-500/20 text-orange-400 border-orange-500/50 mb-3">
              <Flame className="w-3 h-3 mr-1" />
              Trending Now
            </Badge>
            <h2 className="font-heading text-4xl md:text-5xl uppercase tracking-tight">
              What's Hot
            </h2>
          </div>
        </div>

        {/* Trending Beats */}
        {trending.beats.length > 0 && (
          <div className="mb-12">
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-heading text-xl uppercase text-zinc-300 flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-orange-400" />
                Hot Beats
              </h3>
              <Link to="/beats">
                <Button variant="ghost" size="sm" className="text-orange-400 hover:bg-orange-500/10" data-testid="trending-view-all-beats">
                  View All <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </Link>
            </div>
            <div className="space-y-3">
              {trending.beats.slice(0, 4).map((beat, index) => (
                <motion.div
                  key={beat.beat_id}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                >
                  <BeatPlayer beat={beat} />
                </motion.div>
              ))}
            </div>
          </div>
        )}

        {/* Trending Merch */}
        {trending.merch.length > 0 && (
          <div>
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-heading text-xl uppercase text-zinc-300 flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-orange-400" />
                Hot Merch
              </h3>
              <Link to="/merch">
                <Button variant="ghost" size="sm" className="text-orange-400 hover:bg-orange-500/10" data-testid="trending-view-all-merch">
                  View All <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </Link>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
              {trending.merch.slice(0, 4).map((item, index) => (
                <motion.div
                  key={item.merch_id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Link to={`/merch/${item.merch_id}`}>
                    <div className="group relative aspect-[3/4] overflow-hidden bg-zinc-900 border border-white/5 card-hover" data-testid={`trending-merch-${item.merch_id}`}>
                      <img
                        src={item.images?.[0] || ""}
                        alt={item.name}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
                      {item.trending_likes && (
                        <Badge className="absolute top-3 right-3 bg-orange-500/80 text-white border-0 text-xs">
                          <Flame className="w-3 h-3 mr-1" />{item.trending_likes} likes
                        </Badge>
                      )}
                      <div className="absolute bottom-0 left-0 right-0 p-4">
                        <p className="font-medium truncate">{item.name}</p>
                        <div className="flex items-center gap-2">
                          {item.on_sale ? (
                            <>
                              <p className="text-red-400 font-mono text-lg font-bold">${item.price?.toFixed(2)}</p>
                              <p className="text-zinc-500 font-mono text-sm line-through">${item.original_price?.toFixed(2)}</p>
                            </>
                          ) : (
                            <p className="text-neon-magenta font-mono text-lg font-bold">${item.price?.toFixed(2)}</p>
                          )}
                        </div>
                      </div>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default TrendingProducts;
